/**
 * Frigate.jsx — Remastered Frigate NVR & Brain Control Center (SolidJS)
 *
 * Provides real-time camera snapshot grids, AI vision descriptions via Gemini 3.8 Flash,
 * intelligent repeat detection badges, in-widget Qdrant semantic search, and household radar.
 */
import { createSignal, createEffect, onCleanup, onMount, Show, For } from 'solid-js';
import { Portal } from 'solid-js/web';
import useWidgetData from './useWidgetData.js';
import wm from '../lib/wmStore.js';
import './Frigate.css';

function getLabelEmoji(label) {
  const l = String(label || '').toLowerCase();
  if (l.includes('person')) return '🧑';
  if (l.includes('dog')) return '🐕';
  if (l.includes('cat')) return '🐱';
  if (l.includes('car')) return '🚗';
  if (l.includes('package')) return '📦';
  if (l.includes('bird')) return '🐦';
  if (l.includes('bicycle')) return '🚲';
  return '🎯';
}

function fmtTimeAgo(ts) {
  if (!ts) return '';
  const now = Date.now();
  const diffSec = Math.max(0, Math.floor((now - ts) / 1000));
  if (diffSec < 60) return `${diffSec}s ago`;
  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHours = Math.floor(diffMin / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  return `${Math.floor(diffHours / 24)}d ago`;
}

export default function FrigateWidget(props) {
  const { data, loading, refreshing, error, refetch } = useWidgetData('frigate', () => ({}));

  const [activeTab, setActiveTab] = createSignal(props?.settings?.activeTab || 'cameras');
  const [selectedCamId, setSelectedCamId] = createSignal(props?.settings?.selectedCamId || 'e-roto_1');
  const [streamMode, setStreamMode] = createSignal(props?.settings?.streamMode || 'webrtc'); // 'webrtc' | 'snapshot'
  const [streamQuality, setStreamQuality] = createSignal(props?.settings?.streamQuality || 'sub'); // 'sub' | 'main'
  const [audioEnabled, setAudioEnabled] = createSignal(false);
  const [talkEnabled, setTalkEnabled] = createSignal(false);
  const [streamBust, setStreamBust] = createSignal(Date.now());
  const [autoRefresh, setAutoRefresh] = createSignal(true);
  const [snapshotBust, setSnapshotBust] = createSignal(Date.now());
  const [lightboxImg, setLightboxImg] = createSignal(null);
  const [lightboxTitle, setLightboxTitle] = createSignal('');
  const [pageVisible, setPageVisible] = createSignal(typeof document === 'undefined' ? true : !document.hidden);
  const isVisible = () => !props?.widget?.minimized && pageVisible();

  onMount(() => {
    const onVis = () => setPageVisible(!document.hidden);
    document.addEventListener('visibilitychange', onVis);
    onCleanup(() => document.removeEventListener('visibilitychange', onVis));
  });

  createEffect(() => {
    const wid = props?.widget?.id;
    const tab = activeTab();
    const cam = selectedCamId();
    const mode = streamMode();
    const qual = streamQuality();
    if (!wid) return;
    const cur = props?.widget?.settings || {};
    if (
      cur.activeTab !== tab ||
      cur.selectedCamId !== cam ||
      cur.streamMode !== mode ||
      cur.streamQuality !== qual
    ) {
      wm.setSettings(wid, {
        ...cur,
        activeTab: tab,
        selectedCamId: cam,
        streamMode: mode,
        streamQuality: qual,
      });
    }
  });

  // Hardware action state
  const [actionStatus, setActionStatus] = createSignal('');
  const [actionLoading, setActionLoading] = createSignal(false);
  const [wakeStatus, setWakeStatus] = createSignal('');
  const [wakeLoading, setWakeLoading] = createSignal(false);

  // Semantic search state
  const [searchQuery, setSearchQuery] = createSignal('');
  const [searchResults, setSearchResults] = createSignal([]);
  const [searching, setSearching] = createSignal(false);
  const [searchError, setSearchError] = createSignal(null);

  // Auto-refresh camera snapshots every 15 seconds in snapshot mode when visible
  let timer = null;
  createEffect(() => {
    if (timer) {
      clearInterval(timer);
      timer = null;
    }
    if (isVisible() && autoRefresh() && (streamMode() === 'snapshot' || selectedCamId() === 'grid')) {
      timer = setInterval(() => {
        setSnapshotBust(Date.now());
      }, 15000);
    }
  });

  onCleanup(() => {
    if (timer) clearInterval(timer);
  });

  function handleManualRefresh() {
    setSnapshotBust(Date.now());
    setStreamBust(Date.now());
    refetch();
  }

  async function postFrigateApi(endpoint, body = {}) {
    setActionLoading(true);
    setActionStatus(`Transmitting to ${selectedCamId()}…`);
    try {
      const res = await fetch(`/api/v2/frigate/${endpoint}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const json = await res.json();
      if (res.ok && json.ok !== false) {
        setActionStatus(`✓ ${json.action || json.message || 'Command executed successfully'}`);
      } else {
        setActionStatus(`⚠ ${json.error || json.detail || 'Command failed'}`);
      }
      return json;
    } catch (err) {
      setActionStatus(`✕ Error: ${err.message}`);
    } finally {
      setActionLoading(false);
    }
  }

  function handlePtzStep(direction) {
    postFrigateApi(`ptz/${selectedCamId()}`, { action: 'step', direction });
  }

  function handlePtzStop() {
    postFrigateApi(`ptz/${selectedCamId()}`, { action: 'stop' });
  }

  function handlePtzZoom(direction) {
    postFrigateApi(`ptz/${selectedCamId()}`, { action: 'zoom', direction });
  }

  function handlePtzPreset(preset) {
    postFrigateApi(`ptz/${selectedCamId()}`, { action: 'preset', preset });
  }

  function handleLightToggle(targetState) {
    postFrigateApi(`light/${selectedCamId()}`, { state: targetState });
  }

  function handleSirenTrigger(action) {
    if (action === 'siren' || action === 'start') {
      const confirmed = window.confirm(`Sound loud emergency alarm on camera "${selectedCamId()}"?`);
      if (!confirmed) return;
    }
    postFrigateApi(`speak/${selectedCamId()}`, { action });
  }

  function handleSyncClock() {
    postFrigateApi(`sync-time/${selectedCamId()}`);
  }

  async function handleWakeProbe() {
    setWakeLoading(true);
    setWakeStatus('Transmitting wake probe to port 8800…');
    try {
      const res = await fetch(`/api/v2/frigate/wake/${selectedCamId()}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const json = await res.json();
      if (json.state === 'awake') {
        setWakeStatus('● Sensor Awake! Refreshing stream…');
        setStreamBust(Date.now());
      } else {
        setWakeStatus('● Daemon Active on Port 8800 (Sensor in Deep Sleep)');
      }
    } catch (err) {
      setWakeStatus(`Probe error: ${err.message}`);
    } finally {
      setWakeLoading(false);
    }
  }

  function toggleAudio() {
    const next = !audioEnabled();
    setAudioEnabled(next);
    if (!next && talkEnabled()) {
      setTalkEnabled(false);
    }
    setStreamBust(Date.now());
  }

  function toggleTalk() {
    const next = !talkEnabled();
    setTalkEnabled(next);
    if (next) {
      setAudioEnabled(true);
    }
    setStreamBust(Date.now());
  }

  function reconnectStream() {
    setStreamBust(Date.now());
    setSnapshotBust(Date.now());
  }

  async function executeSemanticSearch(e) {
    if (e) e.preventDefault();
    const q = searchQuery().trim();
    if (!q) return;

    setSearching(true);
    setSearchError(null);
    try {
      const res = await fetch(`/api/v2/frigate/search?q=${encodeURIComponent(q)}&limit=8`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();
      setSearchResults(json.results || []);
    } catch (err) {
      setSearchError(err.message || 'Failed to search semantic memory');
    } finally {
      setSearching(false);
    }
  }

  const cameras = () => data()?.cameras || [];
  const activeCam = () => {
    const list = cameras();
    return list.find(c => c.id === selectedCamId()) || list[0] || null;
  };

  const activeStreamSrc = () => {
    const cam = activeCam();
    if (!cam) return 'e-roto_1';
    if (streamQuality() === 'sub' && cam.hasSub) {
      return cam.streamSub || `${cam.id}_sub`;
    }
    return cam.streamName || cam.id;
  };

  const webrtcUrl = () => {
    const cam = activeCam();
    const hasAudio = Boolean(cam?.audio || cam?.speaker);
    const src = activeStreamSrc();
    if (!hasAudio) {
      return `https://frigate.ech-o.net/live/webrtc/webrtc.html?src=${encodeURIComponent(src)}&media=video&t=${streamBust()}`;
    }
    const media = talkEnabled() ? 'video+audio+microphone' : (audioEnabled() ? 'video+audio' : 'video');
    const unmute = (audioEnabled() || talkEnabled()) ? '&unmute=1' : '';
    return `https://frigate.ech-o.net/live/webrtc/webrtc.html?src=${encodeURIComponent(src)}&media=${media}${unmute}&t=${streamBust()}`;
  };

  const recentEvents = () => data()?.recentEvents || [];
  const identities = () => data()?.identities || [];
  const stats = () => data()?.stats || {};
  const brainHealth = () => data()?.brainHealth || {};
  const health = () => data()?.health || {};

  return (
    <div class="v2-widget v2-frigate-widget">
      {/* Header Bar */}
      <div class="frigate-header">
        <div class="frigate-header-left">
          <span class="frigate-title">📹 Frigate NVR & Brain</span>
          <Show when={health()?.up}>
            <span class="frigate-pill frigate-pill--online" title={`Frigate v${health().version}`}>
              ● {cameras().length} Cameras Online
            </span>
          </Show>
          <Show when={brainHealth()?.up}>
            <span class="frigate-pill frigate-pill--brain" title="Active Vision Intelligence Engine">
              🧠 {brainHealth().model || 'gemini-3.8-flash'}
            </span>
          </Show>
        </div>

        <div class="frigate-header-actions">
          <button
            type="button"
            class="v2-tt-btn"
            style={{ padding: '2px 8px', 'font-size': '11px' }}
            onClick={handleManualRefresh}
            title="Refresh camera snapshots and events"
          >
            {refreshing() ? '↻ Fetching…' : '↻ Refresh'}
          </button>
          <a
            href={data()?.serviceUrl || 'https://frigate.ech-o.net'}
            target="_blank"
            rel="noopener noreferrer"
            class="frigate-action-link"
          >
            NVR ↗
          </a>
          <a
            href={data()?.brainUrl || 'http://192.168.68.50:8090'}
            target="_blank"
            rel="noopener noreferrer"
            class="frigate-action-link"
            style={{ color: '#c084fc', 'border-color': 'rgba(168,85,247,0.3)' }}
          >
            Brain SPA ↗
          </a>
        </div>
      </div>

      {/* Tab Navigation */}
      <div class="frigate-tabs-bar">
        <button
          type="button"
          class={`frigate-tab-btn ${activeTab() === 'cameras' ? 'frigate-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('cameras')}
        >
          📹 Live Cameras
        </button>
        <button
          type="button"
          class={`frigate-tab-btn ${activeTab() === 'detections' ? 'frigate-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('detections')}
        >
          ⚡ Detections ({recentEvents().length})
        </button>
        <button
          type="button"
          class={`frigate-tab-btn ${activeTab() === 'search' ? 'frigate-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          🔍 Semantic Search
        </button>
        <button
          type="button"
          class={`frigate-tab-btn ${activeTab() === 'identities' ? 'frigate-tab-btn--active' : ''}`}
          onClick={() => setActiveTab('identities')}
        >
          🐾 Household Radar
        </button>
      </div>

      {/* Main Body */}
      <Show when={!loading()} fallback={<div class="v2-widget-skel" style={{ padding: '20px', 'text-align': 'center' }}>Synchronizing camera telemetry…</div>}>
        <Show when={!error()} fallback={<div class="v2-error" style={{ padding: '16px' }}>{error()}</div>}>
          <div class="frigate-body">
            {/* TAB 1: LIVE CAMERAS */}
            {/* TAB 1: LIVE DECK & CAMERAS */}
            <Show when={activeTab() === 'cameras'}>
              {/* Camera Switcher Tabs */}
              <div class="frigate-sub-bar">
                <div class="frigate-cam-filter">
                  <For each={cameras()}>
                    {(cam) => (
                      <button
                        type="button"
                        class={`frigate-cam-btn ${selectedCamId() === cam.id ? 'frigate-cam-btn--active' : ''}`}
                        onClick={() => {
                          setSelectedCamId(cam.id);
                          setStreamBust(Date.now());
                        }}
                      >
                        <span
                          class="frigate-cam-status-dot"
                          style={{
                            'background-color': cam.status === 'online' ? '#4ade80' : cam.isBattery ? '#fbbf24' : '#ef4444'
                          }}
                        />
                        {cam.name}
                        <Show when={cam.ptz}>
                          <span class="frigate-badge-ptz">PTZ</span>
                        </Show>
                      </button>
                    )}
                  </For>
                  <button
                    type="button"
                    class={`frigate-cam-btn ${selectedCamId() === 'grid' ? 'frigate-cam-btn--active' : ''}`}
                    onClick={() => setSelectedCamId('grid')}
                  >
                    🔲 All Cameras Grid
                  </button>
                </div>

                <label style={{ 'font-size': '10.5px', color: '#94a3b8', display: 'flex', 'align-items': 'center', gap: '4px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={autoRefresh()}
                    onChange={(e) => setAutoRefresh(e.currentTarget.checked)}
                  />
                  Poll Snapshots (15s)
                </label>
              </div>

              {/* SINGLE CAMERA VIEWPORT & DECK */}
              <Show when={selectedCamId() !== 'grid' && activeCam()}>
                {(cam) => (
                  <div class="frigate-interactive-deck">
                    {/* Viewport Box */}
                    <div class="frigate-viewport-box">
                      {/* Standby View for Battery Camera */}
                      <Show
                        when={!cam().isBattery || cam().status === 'online' || cam().wake_state?.awake}
                        fallback={
                          <div class="frigate-battery-standby">
                            <div style={{ 'font-size': '36px', 'margin-bottom': '8px' }}>🔋</div>
                            <h3 style={{ margin: '0 0 6px 0', 'font-size': '15px', color: '#fff', 'font-weight': '700' }}>
                              Battery-Operated Rotational Camera
                            </h3>
                            <div class="frigate-battery-pill">● DEEP SLEEP STANDBY</div>
                            <p style={{ 'max-width': '420px', 'font-size': '11.5px', 'line-height': '1.5', color: '#94a3b8', margin: '0 0 14px 0' }}>
                              This camera operates on internal battery power. Video sensor, ONVIF, and RTSP streams remain asleep until motion is tripped via PIR or actively requested.
                            </p>
                            <div class="frigate-battery-meta">
                              <div><strong>IP:</strong> {cam().ip}</div>
                              <div><strong>Location:</strong> {cam().zone}</div>
                              <div><strong>Daemon:</strong> Port 8800 Active</div>
                            </div>
                            <div style={{ display: 'flex', gap: '10px', 'align-items': 'center' }}>
                              <button
                                type="button"
                                class="frigate-wake-btn"
                                onClick={handleWakeProbe}
                                disabled={wakeLoading()}
                              >
                                {wakeLoading() ? '⚡ Sending Probe…' : '⚡ Send Wake Probe'}
                              </button>
                              <Show when={wakeStatus()}>
                                <span style={{ 'font-size': '11px', color: '#38bdf8' }}>{wakeStatus()}</span>
                              </Show>
                            </div>
                          </div>
                        }
                      >
                        {/* Live WebRTC Stream */}
                        <Show
                          when={streamMode() === 'webrtc' && isVisible()}
                          fallback={
                            <img
                              src={`${cam().snapshotUrl}?t=${snapshotBust()}`}
                              alt={cam().name}
                              class="frigate-viewport-img"
                              onClick={() => {
                                setLightboxImg(`${cam().snapshotUrl}?t=${snapshotBust()}`);
                                setLightboxTitle(`${cam().name} (${cam().zone})`);
                              }}
                            />
                          }
                        >
                          <iframe
                            class="frigate-viewport-iframe"
                            src={webrtcUrl()}
                            allow="autoplay; fullscreen; microphone; camera"
                            title={`Live feed for ${cam().name}`}
                          />
                        </Show>

                        {/* Viewport Overlay */}
                        <div class="frigate-viewport-overlay">
                          <div class="frigate-overlay-row">
                            <span class="frigate-overlay-tag">📹 {cam().name}</span>
                            <div style={{ display: 'flex', gap: '6px', 'align-items': 'center' }}>
                              <Show when={cam().audio || cam().speaker}>
                                <button
                                  type="button"
                                  class={`frigate-audio-btn ${audioEnabled() ? 'frigate-audio-btn--active' : ''}`}
                                  onClick={toggleAudio}
                                  title="Toggle live camera microphone sound"
                                >
                                  {audioEnabled() ? '🔊 Audio Active' : '🔇 Audio Muted'}
                                </button>
                              </Show>
                              <Show when={cam().speaker}>
                                <button
                                  type="button"
                                  class={`frigate-audio-btn ${talkEnabled() ? 'frigate-audio-btn--active' : ''}`}
                                  style={talkEnabled() ? { 'border-color': '#38bdf8', color: '#38bdf8' } : {}}
                                  onClick={toggleTalk}
                                  title="Toggle Two-Way Microphone Talk to camera speaker"
                                >
                                  {talkEnabled() ? '🎤 Talking Live' : '🎤 Two-Way Talk'}
                                </button>
                              </Show>
                              <span class="frigate-overlay-tag" style={{ color: '#4ade80' }}>
                                ● {streamMode() === 'webrtc' ? `LIVE · ${streamQuality().toUpperCase()}` : 'SNAPSHOT'}
                              </span>
                            </div>
                          </div>
                          <div class="frigate-overlay-row">
                            <span class="frigate-overlay-tag" style={{ color: '#38bdf8', 'font-size': '10px' }}>
                              Direct WebRTC · ~50ms (Zero Server CPU)
                            </span>
                            <a
                              href={`https://frigate.ech-o.net/#${cam().id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              class="frigate-overlay-tag"
                              style={{ color: '#f59e0b', 'text-decoration': 'none', 'pointer-events': 'auto' }}
                            >
                              Frigate NVR ↗
                            </a>
                          </div>
                        </div>
                      </Show>
                    </div>

                    {/* Stream Mode & Quality Controls */}
                    <div class="frigate-stream-controls">
                      <div class="frigate-pill-group">
                        <button
                          type="button"
                          class={`frigate-mode-btn ${streamMode() === 'webrtc' ? 'frigate-mode-btn--active' : ''}`}
                          onClick={() => setStreamMode('webrtc')}
                          title="Direct low-latency WebRTC video stream"
                        >
                          ⚡ Live Stream
                        </button>
                        <button
                          type="button"
                          class={`frigate-mode-btn ${streamMode() === 'snapshot' ? 'frigate-mode-btn--active' : ''}`}
                          onClick={() => setStreamMode('snapshot')}
                          title="Low-bandwidth JPEG snapshots polling"
                        >
                          📷 Snapshot
                        </button>
                      </div>

                      <Show when={cam().hasSub && streamMode() === 'webrtc'}>
                        <div class="frigate-pill-group">
                          <button
                            type="button"
                            class={`frigate-mode-btn ${streamQuality() === 'sub' ? 'frigate-mode-btn--active' : ''}`}
                            onClick={() => {
                              setStreamQuality('sub');
                              setStreamBust(Date.now());
                            }}
                            title="Sub-stream: ultra-smooth ~25 FPS with lowest bandwidth"
                          >
                            Sub (Smooth)
                          </button>
                          <button
                            type="button"
                            class={`frigate-mode-btn ${streamQuality() === 'main' ? 'frigate-mode-btn--active' : ''}`}
                            onClick={() => {
                              setStreamQuality('main');
                              setStreamBust(Date.now());
                            }}
                            title="Main-stream: full 1080p high definition"
                          >
                            Main (HD)
                          </button>
                        </div>
                      </Show>

                      <div style={{ display: 'flex', gap: '6px', 'margin-left': 'auto', 'align-items': 'center' }}>
                        <button
                          type="button"
                          class="frigate-tuning-btn"
                          onClick={handleSyncClock}
                          title="Synchronize camera hardware clock via ONVIF"
                        >
                          🕒 Sync Clock
                        </button>
                        <button
                          type="button"
                          class="frigate-tuning-btn"
                          onClick={reconnectStream}
                          title="Reconnect WebRTC live stream"
                        >
                          ↻ Reconnect
                        </button>
                      </div>
                    </div>

                    {/* PTZ Keypad & Auxiliary Deck */}
                    <div class="frigate-ctrl-panel">
                      <div class="frigate-ctrl-header">
                        <strong style={{ 'font-size': '12.5px', color: '#fff', display: 'flex', 'align-items': 'center', gap: '6px' }}>
                          <span>🎮</span> Directional PTZ & Auxiliary Deck
                        </strong>
                        <Show when={cam().ptz} fallback={<span class="frigate-pill" style={{ background: 'rgba(255,255,255,0.05)', color: '#94a3b8' }}>FIXED FOCAL LENS</span>}>
                          <span class="frigate-pill frigate-pill--online">ONVIF PTZ ARMED</span>
                        </Show>
                      </div>

                      {/* Directional D-Pad (for PTZ Cameras) */}
                      <Show when={cam().ptz}>
                        <div class="frigate-ptz-section">
                          <div class="frigate-dpad-container">
                            <div class="frigate-dpad">
                              <button
                                type="button"
                                class="frigate-dpad-btn frigate-dpad-up"
                                onClick={() => handlePtzStep('up')}
                                title="Nudge Up"
                              >
                                🔼
                              </button>
                              <div class="frigate-dpad-middle">
                                <button
                                  type="button"
                                  class="frigate-dpad-btn frigate-dpad-left"
                                  onClick={() => handlePtzStep('left')}
                                  title="Nudge Left"
                                >
                                  ◀️
                                </button>
                                <button
                                  type="button"
                                  class="frigate-dpad-btn frigate-dpad-stop"
                                  onClick={handlePtzStop}
                                  title="Stop All Movement"
                                >
                                  ⏹️
                                </button>
                                <button
                                  type="button"
                                  class="frigate-dpad-btn frigate-dpad-right"
                                  onClick={() => handlePtzStep('right')}
                                  title="Nudge Right"
                                >
                                  ▶️
                                </button>
                              </div>
                              <button
                                type="button"
                                class="frigate-dpad-btn frigate-dpad-down"
                                onClick={() => handlePtzStep('down')}
                                title="Nudge Down"
                              >
                                🔽
                              </button>
                            </div>

                            <div class="frigate-zoom-col">
                              <button
                                type="button"
                                class="frigate-zoom-btn"
                                onClick={() => handlePtzZoom('in')}
                                title="Zoom In"
                              >
                                🔍+ Zoom In
                              </button>
                              <button
                                type="button"
                                class="frigate-zoom-btn"
                                onClick={() => handlePtzZoom('out')}
                                title="Zoom Out"
                              >
                                🔍- Zoom Out
                              </button>
                            </div>
                          </div>

                          {/* Quick Presets */}
                          <Show when={cam().presets && cam().presets.length > 0}>
                            <div class="frigate-presets-row">
                              <span style={{ 'font-size': '11px', color: '#94a3b8', 'font-weight': '600' }}>Presets:</span>
                              <div class="frigate-presets-pills">
                                <For each={cam().presets}>
                                  {(preset) => (
                                    <button
                                      type="button"
                                      class="frigate-preset-btn"
                                      onClick={() => handlePtzPreset(preset)}
                                      title={`Jump to preset: ${preset}`}
                                    >
                                      🎯 {preset}
                                    </button>
                                  )}
                                </For>
                              </div>
                            </div>
                          </Show>
                        </div>
                      </Show>

                      {/* Auxiliary Hardware Controls (Spotlight, Siren) */}
                      <Show when={cam().light || cam().speaker}>
                        <div class="frigate-aux-section">
                          <Show when={cam().light}>
                            <div class="frigate-aux-group">
                              <span style={{ 'font-size': '11px', color: '#cbd5e1', 'font-weight': '600' }}>💡 Spotlight:</span>
                              <button
                                type="button"
                                class="frigate-aux-btn"
                                style={{ background: 'rgba(234,179,8,0.15)', color: '#eab308', 'border-color': 'rgba(234,179,8,0.35)' }}
                                onClick={() => handleLightToggle('on')}
                                title="Turn on camera spotlight/floodlight"
                              >
                                Turn On
                              </button>
                              <button
                                type="button"
                                class="frigate-aux-btn"
                                onClick={() => handleLightToggle('off')}
                                title="Turn off camera spotlight/floodlight"
                              >
                                Turn Off
                              </button>
                            </div>
                          </Show>

                          <Show when={cam().speaker}>
                            <div class="frigate-aux-group">
                              <span style={{ 'font-size': '11px', color: '#cbd5e1', 'font-weight': '600' }}>🚨 Alarm:</span>
                              <button
                                type="button"
                                class="frigate-aux-btn"
                                style={{ background: 'rgba(239,68,68,0.15)', color: '#ef4444', 'border-color': 'rgba(239,68,68,0.35)' }}
                                onClick={() => handleSirenTrigger('siren')}
                                title="Sound emergency siren alert"
                              >
                                Sound Alarm
                              </button>
                              <button
                                type="button"
                                class="frigate-aux-btn"
                                onClick={() => handleSirenTrigger('stop')}
                                title="Silence camera siren"
                              >
                                Silence
                              </button>
                              <button
                                type="button"
                                class="frigate-aux-btn"
                                style={{ color: '#fbbf24', 'border-color': 'rgba(251,191,36,0.35)' }}
                                onClick={() => handleSirenTrigger('chime')}
                                title="Play two-way audio chime on camera speaker"
                              >
                                🔔 Chime
                              </button>
                              <button
                                type="button"
                                class="frigate-aux-btn"
                                style={talkEnabled() ? { background: 'rgba(56,189,248,0.2)', color: '#38bdf8', 'border-color': 'rgba(56,189,248,0.5)' } : { color: '#38bdf8' }}
                                onClick={toggleTalk}
                                title="Toggle live two-way voice microphone"
                              >
                                {talkEnabled() ? '🎤 Stop Talk' : '🎤 Mic Talk'}
                              </button>
                            </div>
                          </Show>
                        </div>
                      </Show>

                      {/* Action status message */}
                      <Show when={actionStatus()}>
                        <div class="frigate-action-status">
                          {actionStatus()}
                        </div>
                      </Show>
                    </div>
                  </div>
                )}
              </Show>

              {/* ALL CAMERAS SNAPSHOT GRID VIEW */}
              <Show when={selectedCamId() === 'grid'}>
                <div class="frigate-grid">
                  <For each={cameras()}>
                    {(cam) => (
                      <div
                        class="frigate-cam-card"
                        onClick={() => {
                          setSelectedCamId(cam.id);
                          setStreamBust(Date.now());
                        }}
                      >
                        <div class="frigate-cam-img-wrap">
                          <img
                            src={`${cam.snapshotUrl}?t=${snapshotBust()}`}
                            alt={cam.name}
                            class="frigate-cam-img"
                            loading="lazy"
                          />
                          <div class="frigate-cam-overlay-top">
                            <span class="frigate-cam-name-badge">{cam.name}</span>
                            <span class="frigate-cam-fps-badge">● {cam.status}</span>
                          </div>
                        </div>
                        <div class="frigate-cam-info">
                          <span class="frigate-cam-zone">📍 {cam.zone}</span>
                          <span style={{ 'font-size': '10px', color: '#38bdf8' }}>Select Camera ↗</span>
                        </div>
                      </div>
                    )}
                  </For>
                </div>
              </Show>
            </Show>

            {/* TAB 2: DETECTIONS FEED */}
            <Show when={activeTab() === 'detections'}>
              <div class="frigate-event-feed">
                <Show when={recentEvents().length > 0} fallback={<div style={{ padding: '20px', 'text-align': 'center', color: '#64748b' }}>No recent detections recorded.</div>}>
                  <For each={recentEvents()}>
                    {(ev) => (
                      <div class="frigate-event-card">
                        <img
                          src={ev.thumbnail_url}
                          alt={ev.label}
                          class="frigate-event-thumb"
                          onClick={() => {
                            setLightboxImg(ev.thumbnail_url);
                            setLightboxTitle(`${ev.label} @ ${ev.camera}`);
                          }}
                        />
                        <div class="frigate-event-details">
                          <div class="frigate-event-meta">
                            <span class="frigate-label-tag">
                              {getLabelEmoji(ev.label)} {ev.label}
                            </span>
                            <span class="frigate-cam-tag">📷 {ev.camera}</span>
                            <span style={{ 'font-size': '10px', color: '#38bdf8' }}>{ev.confidence}%</span>
                            <Show when={ev.is_repeat}>
                              <span class="frigate-repeat-tag">🔁 Repeat x{ev.repeat_count || 1}</span>
                            </Show>
                            <span class="frigate-time-tag">{fmtTimeAgo(ev.timestamp)}</span>
                          </div>
                          <Show when={ev.ai_summary}>
                            <div class="frigate-ai-summary">
                              <span style={{ color: '#c084fc', 'font-weight': '600' }}>🐾 AI Lens: </span>
                              {ev.ai_summary}
                            </div>
                          </Show>
                        </div>
                      </div>
                    )}
                  </For>
                </Show>
              </div>
            </Show>

            {/* TAB 3: SEMANTIC SEARCH */}
            <Show when={activeTab() === 'search'}>
              <div>
                <form class="frigate-search-box" onSubmit={executeSemanticSearch}>
                  <input
                    type="text"
                    class="frigate-search-input"
                    placeholder="Search detections with AI (e.g. 'Creed on deck', 'brown dog', 'white car')..."
                    value={searchQuery()}
                    onInput={(e) => setSearchQuery(e.currentTarget.value)}
                  />
                  <button type="submit" class="frigate-search-btn" disabled={searching()}>
                    {searching() ? 'Searching…' : 'Search'}
                  </button>
                </form>

                <Show when={searchError()}>
                  <div class="v2-error" style={{ 'margin-bottom': '10px' }}>{searchError()}</div>
                </Show>

                <Show when={searchResults().length > 0}>
                  <div class="frigate-event-feed">
                    <For each={searchResults()}>
                      {(item) => (
                        <div class="frigate-event-card">
                          <Show when={item.event_id}>
                            <img
                              src={`/api/v2/frigate/thumbnail/${item.event_id}`}
                              alt={item.label || 'event'}
                              class="frigate-event-thumb"
                              onClick={() => {
                                setLightboxImg(`/api/v2/frigate/thumbnail/${item.event_id}`);
                                setLightboxTitle(`${item.label || 'Detection'} (${Math.round((item.score || 0) * 100)}% match)`);
                              }}
                            />
                          </Show>
                          <div class="frigate-event-details">
                            <div class="frigate-event-meta">
                              <span class="frigate-label-tag">
                                {getLabelEmoji(item.label)} {item.label || 'Match'}
                              </span>
                              <span class="frigate-cam-tag">📷 {item.camera || 'homelab'}</span>
                              <span style={{ 'font-size': '10px', color: '#4ade80', 'font-weight': '700' }}>
                                {Math.round((item.score || 0) * 100)}% Match
                              </span>
                              <span class="frigate-time-tag">{fmtTimeAgo(item.timestamp)}</span>
                            </div>
                            <div class="frigate-ai-summary">
                              {item.summary}
                            </div>
                          </div>
                        </div>
                      )}
                    </For>
                  </div>
                </Show>

                <Show when={!searching() && searchResults().length === 0 && searchQuery()}>
                  <div style={{ padding: '20px', 'text-align': 'center', color: '#64748b', 'font-size': '12px' }}>
                    No matching visual memories found in Qdrant vector database.
                  </div>
                </Show>
              </div>
            </Show>

            {/* TAB 4: HOUSEHOLD RADAR */}
            <Show when={activeTab() === 'identities'}>
              <div class="frigate-identities-grid">
                <For each={identities()}>
                  {(person) => (
                    <div class="frigate-identity-card">
                      <div class="frigate-identity-icon">
                        {person.kind === 'cat' ? '🐱' : person.kind === 'dog' ? '🐕' : '👤'}
                      </div>
                      <div class="frigate-identity-meta">
                        <h4>{person.name}</h4>
                        <div class="frigate-identity-rel">{person.relationship || 'Household Member'}</div>
                        <Show when={person.visual_notes}>
                          <div class="frigate-identity-desc">
                            <strong>Visual:</strong> {person.visual_notes}
                          </div>
                        </Show>
                        <Show when={person.description}>
                          <div class="frigate-identity-desc">
                            {person.description}
                          </div>
                        </Show>
                      </div>
                    </div>
                  )}
                </For>
              </div>
            </Show>
          </div>
        </Show>
      </Show>

      {/* Footer Telemetry */}
      <div class="frigate-footer">
        <div>
          <span>Total Indexed: </span>
          <span class="frigate-footer-stat">{stats()?.total || 0}</span>
          <span style={{ margin: '0 6px' }}>·</span>
          <span>Today: </span>
          <span class="frigate-footer-stat" style={{ color: '#38bdf8' }}>{stats()?.today || 0}</span>
          <span style={{ margin: '0 6px' }}>·</span>
          <span>Filtered Repeats: </span>
          <span class="frigate-footer-stat" style={{ color: '#fbbf24' }}>{stats()?.repeats || 0}</span>
        </div>
        <div>
          <span>Host: </span>
          <span class="frigate-footer-stat">echodedicated</span>
        </div>
      </div>

      {/* Lightbox Modal */}
      <Show when={lightboxImg()}>
        <Portal>
          <div class="frigate-lightbox" onClick={() => setLightboxImg(null)}>
            <div style={{ 'margin-bottom': '10px', color: '#f8fafc', 'font-weight': '600', 'font-size': '13px' }}>
              {lightboxTitle()}
            </div>
            <img
              src={lightboxImg()}
              alt="Enlarged snapshot"
              class="frigate-lightbox-img"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              type="button"
              class="frigate-lightbox-close"
              onClick={() => setLightboxImg(null)}
            >
              ✕ Close Preview
            </button>
          </div>
        </Portal>
      </Show>
    </div>
  );
}
